import { morph, MORPH_OPTIONS } from '@theme/morph';
import { publish } from '@theme/drv-analytics';

const MORPH = { ...MORPH_OPTIONS, childrenOnly: false };

const KEY = 'drv:recent-searches';
const RECENT_LIMIT = 5;
const MIN_TERM = 2;
const DELAY = 250;
const TRACK_DELAY = 3000;

const readTerms = () => {
  try {
    const terms = JSON.parse(localStorage.getItem(KEY));
    return Array.isArray(terms) ? terms.filter((t) => typeof t === 'string') : [];
  } catch {
    return [];
  }
};

const writeTerms = (terms) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(terms));
  } catch {}
};

const remember = (term) => {
  term = (term || '').trim();
  if (term.length < MIN_TERM) return;
  const rest = readTerms().filter((t) => t.toLowerCase() !== term.toLowerCase());
  writeTerms([term, ...rest].slice(0, RECENT_LIMIT));
};

const forget = (term) => writeTerms(readTerms().filter((t) => t !== term));

class DrvSearch extends HTMLElement {
  connectedCallback() {
    if (this.panel) return;
    this.panel = this.querySelector('[data-drv-qs-panel]');
    this.form = this.querySelector('form');
    this.input = this.querySelector('[data-drv-qs-input]');
    this.body = this.querySelector('[data-drv-qs-body]');
    this.status = this.querySelector('[data-drv-qs-status]');
    this.cache = new Map();
    this.seq = 0;
    this.timer = 0;
    this.trackTimer = 0;
    this.pending = null;
    this.tracked = null;
    this.trigger = null;
    this.shownTerm = null;
    const start = this.body.querySelector('[data-drv-qs-view]');
    if (start) {
      this.shownTerm = '';
      this.cache.set('', { controller: new AbortController(), done: true, promise: Promise.resolve(start.outerHTML) });
    }

    document.addEventListener('click', this.onDocumentClick, true);
    document.addEventListener('pointerover', this.onIntent, { passive: true });
    document.addEventListener('touchstart', this.onIntent, { passive: true });
    document.addEventListener('focusin', this.onIntent);
    this.panel.addEventListener('keydown', this.onKeydown);
    this.input.addEventListener('input', () => this.queue());
    this.body.addEventListener('click', this.onBodyClick);
    this.form.addEventListener('submit', this.onSubmit);
    this.querySelector('[data-drv-qs-close]').addEventListener('click', () => this.close());
    this.panel.addEventListener('touchmove', this.onTouchMove, { passive: true });
    window.addEventListener('resize', () => this.isOpen && this.fit());
    document.addEventListener('visibilitychange', () => document.visibilityState === 'hidden' && this.untrack());
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', () => this.isOpen && this.fit());
    }
  }

  get isOpen() {
    return !this.panel.hidden;
  }

  onIntent = (event) => {
    if (this.warm || !(event.target instanceof Element) || !event.target.closest('[data-drv-qs-open]')) return;
    this.warm = true;
    this.body.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
    });
    if (!this.cache.has('')) this.load('').catch(() => {});
  };

  onDocumentClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const opener = event.target.closest('[data-drv-qs-open]');
    if (opener) {
      event.preventDefault();
      event.stopPropagation();
      if (this.isOpen) this.close();
      else this.open(opener);
      return;
    }
    if (this.isOpen && !this.panel.contains(event.target) && event.target.isConnected) this.close(false);
  };

  onKeydown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const items = [...this.panel.querySelectorAll('[data-drv-qs-body] [data-drv-qs-item], [data-drv-qs-recs] [data-drv-qs-item]')].filter((el) => el.getClientRects().length);
    if (!items.length) return;
    event.preventDefault();
    const index = items.indexOf(document.activeElement);
    if (event.key === 'ArrowDown') {
      (items[index + 1] || items[index] || items[0]).focus();
    } else if (index <= 0) {
      this.input.focus();
    } else {
      items[index - 1].focus();
    }
  };

  onBodyClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const remove = event.target.closest('[data-drv-qs-forget]');
    if (remove) {
      const pills = [...this.body.querySelectorAll('[data-drv-qs-forget]')];
      const index = pills.indexOf(remove);
      forget(remove.dataset.drvQsForget);
      this.fillRecent(index);
      return;
    }
    const pill = event.target.closest('[data-drv-qs-term]');
    if (pill) {
      this.run(pill.dataset.drvQsTerm);
      return;
    }
    if (event.target.closest('a[href]')) {
      this.track();
      remember(this.input.value);
    }
  };

  onSubmit = (event) => {
    const term = this.input.value.trim();
    if (!term) {
      event.preventDefault();
      return;
    }
    this.track();
    remember(term);
  };

  onTouchMove = (event) => {
    if (document.activeElement === this.input && !this.form.contains(event.target)) this.input.blur();
  };

  open(trigger) {
    this.trigger = trigger || null;
    this.panel.hidden = false;
    this.mountRecs();
    document.documentElement.classList.add('drv-qs-open');
    this.expanded(true);
    this.fit();
    this.input.focus({ preventScroll: true });
    this.input.select();
    if (this.input.value.trim().length >= MIN_TERM) this.search();
    else if (this.shownTerm === '') this.fillRecent();
    else this.showEmpty();
  }

  mountRecs() {
    const recs = this.panel.querySelector('[data-drv-qs-recs]');
    if (!recs || recs.firstElementChild) return;
    const mount = document.createElement('div');
    mount.setAttribute('data-rebuy-id', recs.dataset.drvQsRecs);
    recs.append(mount);
    let tries = 0;
    const start = () => {
      if (mount.firstElementChild) return;
      const rebuy = window.Rebuy;
      if (rebuy && rebuy._status === 'initialized' && typeof rebuy.init === 'function') {
        rebuy.init();
        return;
      }
      if (++tries < 30) setTimeout(start, 1000);
    };
    start();
  }

  close(restoreFocus = true) {
    if (!this.isOpen) return;
    this.untrack();
    clearTimeout(this.timer);
    this.seq++;
    this.loading(false);
    this.panel.hidden = true;
    document.documentElement.classList.remove('drv-qs-open');
    this.expanded(false);
    if (restoreFocus && this.trigger && this.trigger.isConnected) this.trigger.focus({ preventScroll: true });
  }

  expanded(open) {
    document.querySelectorAll('[data-drv-qs-open]').forEach((el) => el.setAttribute('aria-expanded', String(open)));
    this.input.setAttribute('aria-expanded', String(open));
  }

  fit() {
    const top = Math.max(0, Math.round(this.panel.getBoundingClientRect().top));
    this.panel.style.setProperty('--drv-qs-top', `${top}px`);
    const viewport = window.visualViewport;
    const keyboard = viewport ? Math.max(0, Math.round(window.innerHeight - viewport.height - viewport.offsetTop)) : 0;
    this.panel.style.setProperty('--drv-qs-kb', `${keyboard}px`);
  }

  url(term) {
    if (!term) {
      const url = new URL(this.dataset.rootUrl, window.location.origin);
      url.searchParams.set('section_id', this.dataset.section);
      return url;
    }
    const url = new URL(this.dataset.suggestUrl, window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('section_id', this.dataset.section);
    url.searchParams.set('resources[type]', 'query,product,collection,article,page');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][fields]', 'title,body,tag');
    return url;
  }

  load(term) {
    const key = term.toLowerCase();
    const cached = this.cache.get(key);
    if (cached) return cached.promise;
    const controller = new AbortController();
    const entry = { controller, done: false, promise: null };
    entry.promise = fetch(this.url(term), { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      })
      .then((html) => {
        const template = document.createElement('template');
        template.innerHTML = html;
        const view = template.content.querySelector('[data-drv-qs-view]');
        if (!view) throw new Error('no view');
        entry.done = true;
        return view.outerHTML;
      });
    entry.promise.catch(() => {
      if (this.cache.get(key) === entry) this.cache.delete(key);
    });
    this.cache.set(key, entry);
    return entry.promise;
  }

  prune(term) {
    const current = term.toLowerCase();
    this.cache.forEach((entry, key) => {
      if (!entry.done && key && !current.startsWith(key)) entry.controller.abort();
    });
  }

  queue() {
    clearTimeout(this.timer);
    this.untrack();
    const term = this.input.value.trim();
    this.prune(term);
    if (term.length < MIN_TERM) {
      this.showEmpty();
      return;
    }
    const key = term.toLowerCase();
    if (key === this.shownTerm) {
      this.loading(false);
      this.queueTrack(term);
      return;
    }
    this.loading(true);
    const cached = this.cache.get(key);
    this.timer = setTimeout(() => this.search(), cached && cached.done ? 0 : DELAY);
  }

  run(term) {
    this.untrack();
    this.input.value = term;
    remember(term);
    this.prune(term);
    this.search();
    this.input.focus({ preventScroll: true });
    this.input.select();
  }

  async search() {
    clearTimeout(this.timer);
    const term = this.input.value.trim();
    if (term.length < MIN_TERM) return this.showEmpty();
    const id = ++this.seq;
    this.loading(true);
    try {
      this.show(term, await this.load(term));
    } catch {
      if (id === this.seq) this.loading(false);
    }
  }

  show(term, html) {
    if (!this.isOpen) return;
    const current = this.input.value.trim().toLowerCase();
    const key = term.toLowerCase();
    if (current.length < MIN_TERM || !current.startsWith(key)) return;
    if (key === current) {
      this.render(term, html);
      this.loading(false);
      this.queueTrack(term);
      return;
    }
    const shown = this.shownTerm;
    if (!shown || !current.startsWith(shown) || key.length > shown.length) this.render(term, html);
  }

  async showEmpty() {
    const id = ++this.seq;
    this.loading(false);
    if (this.shownTerm === '') return;
    try {
      const html = await this.load('');
      if (id === this.seq && this.input.value.trim().length < MIN_TERM) this.render('', html);
    } catch {}
  }

  render(term, html) {
    const key = term.toLowerCase();
    const view = this.body.firstElementChild;
    const fresh = this.shownTerm !== key;
    try {
      if (view && view.outerHTML !== html) morph(view, html, MORPH);
      else if (!view) this.body.innerHTML = html;
    } catch {
      this.body.innerHTML = html;
    }
    if (fresh) {
      this.body.scrollTop = 0;
      this.panel.scrollTop = 0;
    }
    this.shownTerm = key;
    this.fillRecent();
    const current = this.body.firstElementChild;
    this.status.textContent = (term && current && current.getAttribute('data-drv-qs-status')) || '';
  }

  loading(on) {
    this.panel.toggleAttribute('data-loading', on);
  }

  queueTrack(term) {
    clearTimeout(this.trackTimer);
    this.pending = term;
    this.trackTimer = setTimeout(() => this.track(), TRACK_DELAY);
  }

  untrack() {
    clearTimeout(this.trackTimer);
    this.pending = null;
  }

  track() {
    const term = this.pending;
    this.untrack();
    if (!term || term.includes('@')) return;
    const key = term.toLowerCase();
    const view = this.body.firstElementChild;
    if (key === this.tracked || key !== this.shownTerm || !view) return;
    this.tracked = key;
    publish('drv_search', {
      search_term: term,
      search_results: Number(view.getAttribute('data-drv-qs-count')) || 0,
    });
  }

  fillRecent(focusIndex) {
    const box = this.body.querySelector('[data-drv-qs-recent]');
    const list = box && box.querySelector('[data-drv-qs-recent-list]');
    if (!list) return;
    const terms = readTerms();
    const label = list.dataset.removeLabel || 'Remove __TERM__';
    box.hidden = terms.length === 0;
    list.replaceChildren(
      ...terms.map((term) => {
        const item = document.createElement('li');
        item.className = 'drv-qs__pill drv-qs__pill--recent';
        const run = document.createElement('button');
        run.type = 'button';
        run.className = 'drv-qs__recent-term';
        run.dataset.drvQsTerm = term;
        run.toggleAttribute('data-drv-qs-item', true);
        run.textContent = term;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'drv-qs__recent-remove';
        remove.dataset.drvQsForget = term;
        remove.setAttribute('aria-label', label.replace('__TERM__', term));
        remove.textContent = '×';
        item.append(run, remove);
        return item;
      })
    );
    if (focusIndex === undefined) return;
    const removes = list.querySelectorAll('[data-drv-qs-forget]');
    (removes[Math.min(focusIndex, removes.length - 1)] || this.input).focus();
  }
}

if (!customElements.get('drv-search')) customElements.define('drv-search', DrvSearch);
